import { useState } from "react";
import axiosService from "../../core/services/axios-service";
import { ApiRoutes } from "../../core/dictionary/api-url";
import {
  orgranizationDepartment,
  orgranizationRole,
} from "../../core/dictionary/orginazation-dropdown";
import ToastService from "../../core/services/toaster-service";
import "../../assets/scss/pre-team-course-survey.scss";

const CONFIDENCE_QUESTIONS = [
  {
    key: "confidenceConsciousness",
    label:
      "Post-course confidence assessing level of consciousness within 5 seconds (always)",
  },
  {
    key: "confidenceAirway",
    label: "Post-course confidence opening the airway (always)",
  },
  {
    key: "confidenceBreathing",
    label:
      "Post-course confidence differentiating between normal and abnormal breathing (always)",
  },
  {
    key: "confidenceCprInitiation",
    label:
      "Post-course confidence calling for help and initiating CPR if there is no pulse or abnormal breathing (always)",
  },
  {
    key: "confidenceCompressions",
    label:
      "Post-course confidence performing chest compressions with correct rate, depth, recoil and minimal interruptions (always)",
  },
  {
    key: "confidenceAedSwitch",
    label:
      "Post-course confidence switching on the AED or defibrillator (always)",
  },
  {
    key: "confidenceAedPads",
    label:
      "Post-course confidence attaching the AED pads to the patient (always)",
  },
  {
    key: "confidenceBackboard",
    label:
      "Post-course confidence placing the patient on a pad or backboard (always)",
  },
  {
    key: "confidenceAedPrompts",
    label:
      "Post-course confidence following the AED voice or visual prompts (always)",
  },
  {
    key: "confidenceShockButtons",
    label:
      "Post-course confidence pressing ANALYZE and SHOCK buttons when appropriate (always)",
  },
  {
    key: "confidenceResumeCompressions",
    label:
      "Post-course confidence quickly resuming compressions immediately after shock (always)",
  },
  {
    key: "confidenceMinimizeInterruptions",
    label:
      "Post-course confidence minimizing interruptions while following the prompts (always)",
  },
  {
    key: "confidenceContinuePrompts",
    label:
      "Post-course confidence continuing as directed by voice or visual prompts from the AED/defibrillator (always)",
  },
];

const initialForm = {
  fullName: "",
  email: "",
  unit: "",
  role: "",
  facility: "",
  confidenceConsciousness: 5,
  confidenceAirway: 5,
  confidenceBreathing: 5,
  confidenceCprInitiation: 5,
  confidenceCompressions: 5,
  confidenceAedSwitch: 5,
  confidenceAedPads: 5,
  confidenceBackboard: 5,
  confidenceAedPrompts: 5,
  confidenceShockButtons: 5,
  confidenceResumeCompressions: 5,
  confidenceMinimizeInterruptions: 5,
  confidenceContinuePrompts: 5,
};

export default function PostTeamCourseSurvey() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "range" ? Number(value) : value,
    }));
  };

  const validate = () => {
    if (!form.fullName.trim()) return "Full Name is required.";
    if (!form.email.trim()) return "Email is required.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email))
      return "Please enter a valid email address.";
    if (!form.unit) return "Unit is required.";
    if (!form.role) return "Role is required.";
    if (!form.facility.trim()) return "Facility is required.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        "Full Name": form.fullName,
        Email: form.email,
        Unit: form.unit,
        Role: form.role,
        Facility: form.facility,
        "Post-course confidence assessing level of consciousness within 5 seconds (always)":
          form.confidenceConsciousness,
        "Post-course confidence opening the airway (always)":
          form.confidenceAirway,
        "Post-course confidence differentiating between normal and abnormal breathing (always)":
          form.confidenceBreathing,
        "Post-course confidence calling for help and initiating CPR if there is no pulse or abnormal breathing (always)":
          form.confidenceCprInitiation,
        "Post-course confidence performing chest compressions with correct rate, depth, recoil and minimal interruptions (always)":
          form.confidenceCompressions,
        "Post-course confidence switching on the AED or defibrillator (always)":
          form.confidenceAedSwitch,
        "Post-course confidence attaching the AED pads to the patient (always)":
          form.confidenceAedPads,
        "Post-course confidence placing the patient on a pad or backboard (always)":
          form.confidenceBackboard,
        "Post-course confidence following the AED voice or visual prompts (always)":
          form.confidenceAedPrompts,
        "Post-course confidence pressing ANALYZE and SHOCK buttons when appropriate (always)":
          form.confidenceShockButtons,
        "Post-course confidence quickly resuming compressions immediately after shock (always)":
          form.confidenceResumeCompressions,
        "Post-course confidence minimizing interruptions while following the prompts (always)":
          form.confidenceMinimizeInterruptions,
        "Post-course confidence continuing as directed by voice or visual prompts from the AED/defibrillator (always)":
          form.confidenceContinuePrompts,
      };

      const res = await axiosService.post(ApiRoutes.POST_COURSE_SURVEY, payload);
      if (res.data && res.data.success) {
        ToastService.success(
          res.data.message || "Survey submitted successfully!"
        );
        setSubmitted(true);
      } else {
        const msg =
          res.data?.message || "Failed to submit survey. Please try again.";
        setError(msg);
        ToastService.error(msg);
      }
    } catch (err) {
      const msg = err?.message || "Server error. Please try again.";
      setError(msg);
      ToastService.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="survey-page">
      {/* Header */}
      <header className="survey-header">
        <img
          src="https://res.cloudinary.com/dhhzpdckr/image/upload/v1758128880/rescuernlogo_dsi3mf.png"
          alt="RescueRn Logo"
          className="survey-header__logo"
        />
      </header>

      {/* Main content */}
      <main className="survey-main">
        {submitted ? (
          <div className="survey-thankyou">
            <div className="survey-thankyou__icon">✓</div>
            <h2 className="survey-thankyou__title">Thank You!</h2>
            <p className="survey-thankyou__message">
              Your post-course survey has been submitted successfully. We
              appreciate your feedback and hope the training was valuable.
            </p>
          </div>
        ) : (
          <div className="survey-card">
            <div className="survey-card__intro">
              <h1 className="survey-card__title">Post-Course Survey</h1>
              <p className="survey-card__subtitle">
                Please complete this short survey after your resuscitation
                training session.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {/* Section 1: Personal Information */}
              <div className="survey-section">
                <h3 className="survey-section__heading">
                  Personal Information
                </h3>

                <div className="survey-row">
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="fullName">
                      Full Name <span className="survey-required">*</span>
                    </label>
                    <input
                      id="fullName"
                      type="text"
                      name="fullName"
                      className="survey-input"
                      placeholder="Enter your full name"
                      value={form.fullName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="email">
                      Email <span className="survey-required">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      className="survey-input"
                      placeholder="Enter your email"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="survey-row">
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="unit">
                      Unit <span className="survey-required">*</span>
                    </label>
                    <select
                      id="unit"
                      name="unit"
                      className="survey-select"
                      value={form.unit}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Unit</option>
                      {orgranizationDepartment.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="role">
                      Role <span className="survey-required">*</span>
                    </label>
                    <select
                      id="role"
                      name="role"
                      className="survey-select"
                      value={form.role}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Role</option>
                      {orgranizationRole.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="survey-field survey-field--full">
                  <label className="survey-label" htmlFor="facility">
                    Facility <span className="survey-required">*</span>
                  </label>
                  <input
                    id="facility"
                    type="text"
                    name="facility"
                    className="survey-input"
                    placeholder="Enter your facility name"
                    value={form.facility}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Section 2: Confidence Assessment */}
              <div className="survey-section">
                <h3 className="survey-section__heading">
                  Confidence Assessment
                </h3>
                <p className="survey-section__description">
                  Rate your confidence level from <strong>1</strong> (not
                  confident) to <strong>10</strong> (very confident).
                </p>

                {CONFIDENCE_QUESTIONS.map((q) => (
                  <div
                    key={q.key}
                    className="survey-field survey-field--full survey-slider-field"
                  >
                    <label className="survey-label survey-slider-label">
                      {q.label}
                    </label>
                    <div className="survey-slider-row">
                      <span className="survey-slider-edge">1</span>
                      <input
                        type="range"
                        name={q.key}
                        min="1"
                        max="10"
                        step="1"
                        value={form[q.key]}
                        onChange={handleChange}
                        className="survey-slider"
                      />
                      <span className="survey-slider-edge">10</span>
                      <span className="survey-slider-value">{form[q.key]}</span>
                    </div>
                  </div>
                ))}
              </div>

              {error && <div className="survey-error">{error}</div>}

              <button
                type="submit"
                className="survey-submit-btn"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Survey"}
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
